import React, { useState, useEffect } from 'react';
import { Megaphone, Search, Plus, Edit2, Trash2, X as XIcon } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

const getAuthHeader = () => {
  const token = localStorage.getItem('adminToken');
  return { Authorization: `Bearer ${token}` };
};

const AnnouncementsManager = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    type: 'news',
    priority: 'medium',
    targetAudience: 'all',
    isActive: true,
    validFrom: new Date().toISOString().split('T')[0],
    validUntil: '',
    imageUrl: '',
    link: ''
  });

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/announcements`, {
        headers: getAuthHeader()
      });
      setAnnouncements(response.data);
    } catch (error) {
      console.error('Failed to fetch announcements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (announcement = null) => {
    if (announcement) {
      setEditingAnnouncement(announcement);
      setFormData({
        title: announcement.title,
        content: announcement.content,
        type: announcement.type,
        priority: announcement.priority,
        targetAudience: announcement.targetAudience,
        isActive: announcement.isActive,
        validFrom: announcement.validFrom ? new Date(announcement.validFrom).toISOString().split('T')[0] : '',
        validUntil: announcement.validUntil ? new Date(announcement.validUntil).toISOString().split('T')[0] : '',
        imageUrl: announcement.imageUrl || '',
        link: announcement.link || ''
      });
    } else {
      setEditingAnnouncement(null);
      setFormData({
        title: '',
        content: '',
        type: 'news',
        priority: 'medium',
        targetAudience: 'all',
        isActive: true,
        validFrom: new Date().toISOString().split('T')[0],
        validUntil: '',
        imageUrl: '',
        link: ''
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingAnnouncement) {
        await axios.put(
          `${API}/api/admin/announcements/${editingAnnouncement._id}`,
          formData,
          { headers: getAuthHeader() }
        );
        alert('Announcement updated successfully');
      } else {
        await axios.post(
          `${API}/api/admin/announcements`,
          formData,
          { headers: getAuthHeader() }
        );
        alert('Announcement created successfully');
      }
      
      setShowModal(false);
      setEditingAnnouncement(null);
      fetchAnnouncements();
    } catch (error) {
      alert('Failed to save announcement: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteAnnouncement = async (announcementId) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) {
      return;
    }

    try {
      await axios.delete(`${API}/api/admin/announcements/${announcementId}`, {
        headers: getAuthHeader()
      });
      alert('Announcement deleted successfully');
      fetchAnnouncements();
    } catch (error) {
      alert('Failed to delete announcement');
    }
  };

  const filteredAnnouncements = announcements.filter(announcement =>
    announcement.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    announcement.content.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getTypeColor = (type) => {
    switch(type) {
      case 'offer': return 'bg-green-500/20 text-green-400';
      case 'news': return 'bg-blue-500/20 text-blue-400';
      case 'alert': return 'bg-red-500/20 text-red-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  if (loading) {
    return <div className="text-white text-center py-12">Loading announcements...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-black text-white mb-2">Announcements Manager</h1>
          <p className="text-gray-400">Manage offers, news, and alerts for agents</p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="flex items-center space-x-2 px-6 py-3 bg-yellow-400 hover:bg-yellow-500 text-black font-bold rounded-lg transition-colors"
        >
          <Plus size={20} />
          <span>New Announcement</span>
        </button>
      </div>

      <div className="relative">
        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
        <input
          type="text"
          placeholder="Search announcements..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-12 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
          <p className="text-gray-400 text-sm">Total</p>
          <p className="text-2xl font-bold text-white">{announcements.length}</p>
        </div>
        <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
          <p className="text-green-400 text-sm">Offers</p>
          <p className="text-2xl font-bold text-green-400">
            {announcements.filter(a => a.type === 'offer').length}
          </p>
        </div>
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
          <p className="text-blue-400 text-sm">News</p>
          <p className="text-2xl font-bold text-blue-400">
            {announcements.filter(a => a.type === 'news').length}
          </p>
        </div>
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
          <p className="text-red-400 text-sm">Active</p>
          <p className="text-2xl font-bold text-red-400">
            {announcements.filter(a => a.isActive).length}
          </p>
        </div>
      </div>

      {filteredAnnouncements.length === 0 ? (
        <div className="text-center py-12">
          <Megaphone className="mx-auto mb-4 text-gray-600" size={48} />
          <p className="text-gray-400">No announcements found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredAnnouncements.map((announcement) => (
            <div
              key={announcement._id}
              className="bg-gray-800 border border-gray-700 rounded-xl p-6"
            >
              <div className="flex justify-between items-start mb-3">
                <span className={`px-2 py-1 rounded text-xs font-bold ${getTypeColor(announcement.type)}`}>
                  {announcement.type.toUpperCase()}
                </span>
                <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                  announcement.isActive ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                }`}>
                  {announcement.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>

              <h3 className="text-white font-bold text-lg mb-2">{announcement.title}</h3>
              <p className="text-gray-400 text-sm mb-4 line-clamp-3">{announcement.content}</p>

              <div className="flex space-x-2">
                <button
                  onClick={() => handleOpenModal(announcement)}
                  className="flex-1 p-2 hover:bg-blue-500/20 rounded-lg text-blue-400"
                >
                  <Edit2 size={16} className="mx-auto" />
                </button>
                <button
                  onClick={() => handleDeleteAnnouncement(announcement._id)}
                  className="flex-1 p-2 hover:bg-red-500/20 rounded-lg text-red-400"
                >
                  <Trash2 size={16} className="mx-auto" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-gray-800 rounded-xl max-w-2xl w-full my-8">
            <div className="p-6 border-b border-gray-700 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-white">
                {editingAnnouncement ? 'Edit Announcement' : 'Create Announcement'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-gray-700 rounded-lg text-gray-400"
              >
                <XIcon size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="text-white font-medium mb-2 block">Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                />
              </div>

              <div>
                <label className="text-white font-medium mb-2 block">Content *</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  required
                  rows={4}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-white font-medium mb-2 block">Type</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                  >
                    <option value="offer">Offer</option>
                    <option value="news">News</option>
                    <option value="alert">Alert</option>
                  </select>
                </div>

                <div>
                  <label className="text-white font-medium mb-2 block">Priority</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>

                <div>
                  <label className="text-white font-medium mb-2 block">Target</label>
                  <select
                    value={formData.targetAudience}
                    onChange={(e) => setFormData({ ...formData, targetAudience: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                  >
                    <option value="all">All Agents</option>
                    <option value="tier1">Tier 1</option>
                    <option value="tier2">Tier 2</option>
                    <option value="tier3">Tier 3</option>
                  </select>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="w-5 h-5"
                  />
                  <label htmlFor="isActive" className="ml-2 text-white">Active</label>
                </div>
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-3 bg-yellow-400 hover:bg-yellow-500 text-black font-bold rounded-lg"
                >
                  {editingAnnouncement ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnnouncementsManager;
